import { OrganizationRole } from 'src/context/types';
import { useAuthSession, useOrganizationRoleState } from 'src/hooks/useAuthSession';

export type Permissions = {
  isPlatformAdmin: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isMember: boolean;
  canManageOrg: boolean;
  canManageRestaurant: boolean;
  canManageRestaurantSettings: boolean;
  canViewAllBranches: boolean;
};

/**
 * Pure so it can be tested without a session. Capability names, not role names, are what
 * components should branch on — the role-to-capability mapping is allowed to move, the
 * call sites are not.
 */
export function derivePermissions(
  organizationRole: OrganizationRole | null,
  platformRole: 'user' | 'admin'
): Permissions {
  const isPlatformAdmin = platformRole === 'admin';
  const isOwner = organizationRole === 'owner';
  const isAdmin = organizationRole === 'admin';
  const isMember = organizationRole === 'member';
  const manages = isOwner || isAdmin || isPlatformAdmin;

  return {
    isPlatformAdmin,
    isOwner,
    isAdmin,
    isMember,
    canManageOrg: manages,
    canManageRestaurant: manages,
    canManageRestaurantSettings: isOwner || isPlatformAdmin,
    canViewAllBranches: manages,
  };
}

export function isPermissionsReady(input: {
  sessionPending: boolean;
  rolePending: boolean;
  roleHealing: boolean;
}): boolean {
  return !input.sessionPending && !input.rolePending && !input.roleHealing;
}

export function useAuthPermissions(): Permissions & { ready: boolean } {
  const { platformRole, isPending } = useAuthSession();
  const { role: organizationRole, ready: roleReady } = useOrganizationRoleState();
  return {
    ...derivePermissions(organizationRole, platformRole),
    ready: isPermissionsReady({ sessionPending: isPending, rolePending: !roleReady, roleHealing: false }),
  };
}
